import { Inject, Injectable } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Brand } from 'src/brands/brand';

/** URL query parameter that overrides the browser's languages, e.g. `/dashboard/<jwt>?lang=de-CH` (or just `?lang=de`). */
export const LOCALE_QUERY_PARAM = 'lang';
/** sessionStorage key remembering an explicitly chosen culture (via LOCALE_QUERY_PARAM or select()) for the rest of the session. */
export const LOCALE_STORAGE_KEY = 'locale';

/**
 * Picks the culture (e.g. "sv-SE") requested from the CMS/backend, in order of preference: the `?lang=` query parameter,
 * a culture chosen earlier this session, the browser's languages — each only if the brand supports it — otherwise
 * Brand.defaultLocale. Resolved once at startup and exposed app-wide as LOCALE_ID (see app.module.ts), so consumers
 * should inject LOCALE_ID rather than this service; changing it means reloading the page (see select()).
 */
@Injectable({
  providedIn: 'root'
})
export class LocaleService {

  readonly current: string;
  readonly supported: readonly string[] = Brand.supportedLocales;

  constructor(@Inject(DOCUMENT) private document: Document) {
    const requested = new URLSearchParams(document.location.search).get(LOCALE_QUERY_PARAM);
    const requestedSupported = requested ? resolveLocale([requested], this.supported, '') : '';
    if (requestedSupported) {
      sessionStorage.setItem(LOCALE_STORAGE_KEY, requestedSupported);
    }

    const stored = sessionStorage.getItem(LOCALE_STORAGE_KEY);
    const browserLanguages = navigator.languages?.length ? navigator.languages : [navigator.language];
    const preferred = [requestedSupported, stored ?? '', ...browserLanguages].filter(language => language);
    this.current = resolveLocale(preferred, this.supported, Brand.defaultLocale);
    document.documentElement.lang = this.current;
  }

  /** Remembers `culture` for the rest of the session and reloads the page, dropping any `?lang=` that would override it. */
  select(culture: string): void {
    if (culture === this.current) {
      return;
    }

    sessionStorage.setItem(LOCALE_STORAGE_KEY, culture);
    const url = new URL(this.document.location.href);
    url.searchParams.delete(LOCALE_QUERY_PARAM);
    this.document.location.assign(url.toString());
  }
}

/** For each preferred language in order, returns an exact supported match ("de-CH"), else a same-language one ("de-DE" -> "de-CH"); otherwise the fallback. */
export function resolveLocale(preferred: readonly string[], supported: readonly string[], fallback: string): string {
  for (const language of preferred) {
    const wanted = language.toLowerCase();
    const exact = supported.find(s => s.toLowerCase() === wanted);
    if (exact) {
      return exact;
    }

    const primary = wanted.split('-')[0];
    const sameLanguage = supported.find(s => s.toLowerCase().split('-')[0] === primary);
    if (sameLanguage) {
      return sameLanguage;
    }
  }

  return fallback;
}

/** The culture's name in its own language, e.g. "de-CH" -> "Deutsch (Schweiz)". */
export function localeDisplayName(culture: string): string {
  const name = new Intl.DisplayNames([culture], { type: 'language', languageDisplay: 'standard' }).of(culture) ?? culture;
  return name.charAt(0).toLocaleUpperCase(culture) + name.slice(1);
}
